import { ChangeEvent, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IconFile } from 'UIPackage/IconsModule/components/IconFile';
import { addNoticeKnmFileSizeErrorDetailed } from 'CorePackage/NotificationModule/common/addNoticeKnmFileSizeErrorSmall';

import styles from './KnmAttachments.module.scss';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { getMaxFileSize } from '../../../CorePackage/MainModule/common/getMaxFileSize';
import { getKnmSystemType } from '../../MainModule/selectors/knmSelectors';
import { KnmSystemTypesEnum } from '../../MainModule/enums/KnmSystemTypesEnum';
import { MB } from '../../../CorePackage/MainModule/constants/fileSize';

interface IKnmSignDocumentModalProps {
  document: File | null;
  handleRemoveDocument: () => void;
  handleRemoveSign?: (index: number) => () => void;
  handleSetAttachment: (file: File, isSign: boolean) => void;
  onlyDoc?: boolean;
  signs: Array<File>;
}

interface IKnmSignDocumentModalStoreProps {
  knmSystemType: KnmSystemTypesEnum | undefined;
  settings: IAdminSetting[];
}

const KnmAttachmentsWithoutStore = ({
  document,
  handleRemoveDocument,
  handleRemoveSign,
  handleSetAttachment,
  onlyDoc,
  signs,
  knmSystemType,
  settings,
}: IKnmSignDocumentModalProps & IKnmSignDocumentModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const maxFileSize = getMaxFileSize(settings, knmSystemType);

  const handleFileSizeError = useCallback(() => {
    dispatch(addNoticeKnmFileSizeErrorDetailed(maxFileSize));
  }, [dispatch, maxFileSize]);

  const renderAttachment = (attachment: File, index?: number) => (
    <div className={styles.AttachedDocument} key={index || attachment.name}>
      <IconFile width="small" fileName={attachment.name} />
      <span className={styles.FileName}>{attachment.name}</span>
      <button
        className={styles.RemoveButton}
        type="button"
        onClick={handleRemoveSign && index !== undefined ? handleRemoveSign(index) : handleRemoveDocument}
      >
        x
      </button>
    </div>
  );

  const handleInputAttachment = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const file = e.target.files[0];
        const isSizeValid = file.size <= maxFileSize * MB;
        const isSign = file.name.split('.').pop() === 'sig';

        if (!isSizeValid) {
          handleFileSizeError();
        } else {
          handleSetAttachment(file, isSign);
        }
      }
      e.target.value = '';
    },
    [dispatch, handleSetAttachment],
  );

  return (
    <div className={styles.Attachments}>
      <div className={styles.AttachmentsContainer}>
        <div className={styles.AttachmentHeader}>
          <span>Документ</span>
          <label className={styles.AddAttachmentLabel} htmlFor="document">
            Добавить
          </label>
          <input
            id="document"
            name="document"
            type="file"
            accept=".ods,.xls,.xlsx,.doc,.docx,.pdf"
            style={{ display: 'none' }}
            onChange={handleInputAttachment}
          />
        </div>
        {document && renderAttachment(document)}
      </div>
      {!onlyDoc && (
        <div className={styles.AttachmentsContainer}>
          <div className={styles.AttachmentHeader}>
            <span>Подписи</span>
            <label className={styles.AddAttachmentLabel} htmlFor="signs">
              Добавить
            </label>
            <input
              id="signs"
              name="signs"
              type="file"
              accept=".sig"
              style={{ display: 'none' }}
              onChange={handleInputAttachment}
            />
          </div>
          {signs.length ? signs.map((sign, idx) => renderAttachment(sign, idx)) : null}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState): IKnmSignDocumentModalStoreProps => ({
  knmSystemType: getKnmSystemType(state),
  settings: getAdminFetchSettings(state),
});

export const KnmAttachments = connect(mapStateToProps)(KnmAttachmentsWithoutStore);
